'use client';

import React, { FC, useEffect, useState } from 'react';
import ItineraryAccordion from '@/components/itinerary/ItineraryAccordion';
import { facArrowDown } from '@/Theme/SVG/Icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GridContainer from '@/Layouts/GridContainer';
import { ApiItinerary, ApiProduct } from '../../../api/model';
import { useTranslation } from '@/app/i18n/client';
import Keys from '@/Translations/generated/da/product.json.keys';
import HeroKeys from '@/Translations/generated/da/hero.json.keys';
import PriceInfoKeys from '@/Translations/generated/da/priceInformation.json.keys';
import { Props as PageProps } from '@/types/cms/magnolia';
import { useProductPageContext } from '@/components/productPageContext/ProductPageContext';
import { Spinner } from 'reactstrap';
import { createPdf } from '@/components/itinerary/createPdf';
import { ApiProductsCacheResponse } from '@ibe/api';

type Props = {
  theme: string;
  itinerary: ApiItinerary[];
  product?: ApiProduct;
  pageProps?: PageProps;
  selectedPackage?: ApiProductsCacheResponse;
};

let timer: ReturnType<typeof setTimeout> | null = null;

const Itinerary: FC<Props> = ({ theme, itinerary, product, pageProps, selectedPackage }) => {
  const { t } = useTranslation('product');
  const { t: tHero } = useTranslation('hero');
  const { t: tPriceInfo } = useTranslation('priceInformation');
  const { albConfig: { useNewPdf } = {}, locale } = pageProps || {};

  const { printMode, setPrintMode } = useProductPageContext();
  const [allOpen, setAllOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');

  useEffect(() => {
    function afterPrint() {
      setPrintMode(false);
    }
    window.addEventListener('afterprint', afterPrint);

    return (): void => {
      window.removeEventListener('afterprint', afterPrint);

      if (!!timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    if (printMode) {
      timer = setTimeout(function () {
        window.print();
      }, 6000);
    }
  }, [printMode]);

  const onClick = () => {
    setPrintMode(true);
  };

  const requestPdf = async (): Promise<void> => {
    setIsLoading(true);
    const buffer = await createPdf({
      translations: {
        productAirportLabel: tHero(HeroKeys.departure),
        productDateLabel: tHero(HeroKeys.date),
        productDurationLabel: tHero(HeroKeys.duration),
        productLanguageLabel: tHero(HeroKeys.language),
        productPriceLabel: tHero(HeroKeys.price),
        days: tHero(HeroKeys.days),
        dailyProgram: t(Keys.dailyProgram),
        highlights: t(Keys.highlights),
        sections: {
          introduction: t(Keys.introduction),
          priceInformation: t(Keys.priceInformation)
        },
        priceInformation: {
          tripPrice: t(PriceInfoKeys.tripPrice),
          singleFee: t(PriceInfoKeys.singleFee),
          childPrice: t(PriceInfoKeys.childPrice),
          roomUpgrade: t(PriceInfoKeys.roomUpgrade),
          priceInfoExcursions: t(PriceInfoKeys.priceInfoExcursions),
          priceIncludes: t(PriceInfoKeys.priceIncludes),
          priceDoesNotInclude: t(PriceInfoKeys.priceDoesNotInclude),
          date: t(PriceInfoKeys.date),
          price: t(PriceInfoKeys.price)
        }
      },
      locale: locale || '',
      product,
      pacificProduct: selectedPackage
    });
    const blob = new Blob([new Uint8Array(buffer)], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    setPdfUrl(url);
    setIsLoading(false);
  };

  const handlePDFCreation = async () => {
    if (useNewPdf) {
      await requestPdf();
    } else {
      onClick();
    }
  };

  return (
    <GridContainer className={`itinerary ${theme}`}>
      <div className="itinerary__wrapper">
        <h2 className="itinerary__header">{t(Keys.dailyProgram)}</h2>
        <div>
          <button
            className="btn btn-primary itinerary__btn"
            onClick={handlePDFCreation}
            aria-label="print page"
          >
            <div>
              <span className="itinerary__btn__text">
                {useNewPdf && isLoading ? t(Keys.pdfIsBeingCreated) : t(Keys.downloadFullProgram)}
              </span>
              {printMode || (useNewPdf && isLoading) ? (
                <Spinner className="itinerary__btn__spinner"></Spinner>
              ) : (
                <FontAwesomeIcon icon={facArrowDown} />
              )}
            </div>
          </button>
          <div className="mt-xs-2">
            {pdfUrl ? (
              <a href={pdfUrl} target="_blank">
                {t(Keys.downloadPdf)}
              </a>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      <div className="itinerary__showAll__btn">
        <button
          className="btn btn-secondary "
          onClick={() => setAllOpen(!allOpen)}
          aria-label="open all itinerary accordions"
        >
          <span className="itinerary__showAll__btn__text">
            {allOpen ? t(Keys.closeAll) : t(Keys.expandAll)}
          </span>
        </button>
      </div>
      {!!itinerary && itinerary.length > 0 && (
        <ItineraryAccordion
          groups={itinerary}
          product={product}
          setAllOpen={setAllOpen}
          allOpen={allOpen}
        />
      )}
    </GridContainer>
  );
};

export default Itinerary;
